import {
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonTextComponent,
} from '@domgen/dgx-fe-components/button';

import { BadgeComponent } from '@domgen/dgx-fe-components/badge';
import { RichTextComponent } from '@domgen/dgx-fe-components/content';
import { VisibilityService } from '@domgen/dgx-fe-components/general';
import {
  GridColumnComponent,
  GridRowComponent,
} from '@domgen/dgx-fe-components/grid';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import {
  BenefitListItem,
  BenefitsListComponent,
} from '@domgen/dgx-fe-components/list';
import { LoadingOverlayComponent } from '@domgen/dgx-fe-components/loading';
import {
  MODAL_DATA,
  ModalComponent,
  ModalReference,
} from '@domgen/dgx-fe-components/modal';
import { PanelComponent } from '@domgen/dgx-fe-components/panel';
import { GAClickDirective, GAEvent, GAService } from '@domgen/dgx-fe-ga';
import {
  CurrencyFormatPipe,
  I18nService,
  provideTranslationDictionaryMapping,
  TranslatePipe,
} from '@domgen/dgx-fe-i18n';
import {
  QuoteOfferPriceDiscountedPipe,
  RegisteredItemMonthlyOrAnnuallyLabelPipe,
  USCheckoutService,
} from '@us-registration/data-access';
import {
  PrepaySubscriptionVariant,
  ProtectionDetailsModalBenefitsListItem,
  ProtectionDetailsUpsellModalData,
  ProtectionDetailsUpsellModalResult,
  USPostHogTrackingEvents,
} from '@us-registration/util-models';
import posthog from 'posthog-js';
import { translation } from '../../i18n/translation.constant';
import { ProtectionDetailsPurchasePriceOptionComponent } from '../protection-details-purchase-price-option/protection-details-purchase-price-option.component';

@Component({
  templateUrl: './protection-details-upsell-modal.component.html',
  styleUrls: ['./protection-details-upsell-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BadgeComponent,
    BenefitsListComponent,
    ButtonComponent,
    ButtonTextComponent,
    CurrencyFormatPipe,
    GAClickDirective,
    GridColumnComponent,
    GridRowComponent,
    IconComponent,
    LoadingOverlayComponent,
    NgFor,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    PanelComponent,
    ProtectionDetailsPurchasePriceOptionComponent,
    QuoteOfferPriceDiscountedPipe,
    RegisteredItemMonthlyOrAnnuallyLabelPipe,
    RichTextComponent,
    TranslatePipe,
  ],
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class ProtectionDetailsUpsellModalComponent
  extends ModalComponent
  implements OnInit, AfterViewInit
{
  benefits: BenefitListItem[] = [];
  usCheckoutService = inject(USCheckoutService);
  ctaUpsellLoading = false;
  @ViewChild('modal') modalRef!: ElementRef<HTMLElement>;

  readonly prepaySubscriptionVariant: typeof PrepaySubscriptionVariant =
    PrepaySubscriptionVariant;

  constructor(
    @Inject(MODAL_DATA) readonly modalData: ProtectionDetailsUpsellModalData,
    protected readonly modalReference: ModalReference<
      ProtectionDetailsUpsellModalComponent,
      ProtectionDetailsUpsellModalResult
    >,
    protected readonly elementRef: ElementRef,
    protected readonly gaService: GAService,
    protected readonly i18nService: I18nService,
    protected readonly visibilityService: VisibilityService
  ) {
    super(elementRef, visibilityService);
  }

  ngOnInit(): void {
    this.gaService.pageView({
      pagename: 'protection-details-upsell-popup',
    });

    this.gaService.tag({
      eventCategory: 'modalUpsell',
      eventAction: 'modal-presented',
      eventLabel: 'registration-only',
      pagename: 'protection-details-upsell-popup',
      event: GAEvent.GenericEvent,
    });

    this.benefits =
      this.modalData.cmsProtectionDetailsUpsellModal.protectionDetailsModalBenefitsListItems.map(
        (benefit: ProtectionDetailsModalBenefitsListItem): BenefitListItem => ({
          benefit: benefit.item,
          included: true,
        })
      );
  }

  ngAfterViewInit() {
    this.modalRef.nativeElement.scrollTop = 0;
  }

  handleModalPaymentOptionAction(clickedPrepayCta: boolean): void {
    this.continueWithExtraProtection(clickedPrepayCta);
  }

  continueWithExtraProtection(prepayCtaClicked = false): void {
    posthog.capture('protection-details-upsell-cta-clicked');
    posthog.capture(
      prepayCtaClicked
        ? USPostHogTrackingEvents.ProtectionDetailsModalAnnualUpsellCtaClicked
        : USPostHogTrackingEvents.ProtectionDetailsModalMonthlyUpsellCtaClicked
    );

    this.ctaUpsellLoading = true;
    this.usCheckoutService.redirectToCheckout(
      this.modalData.promotionalPriceEnabled,
      this.modalData.systemCode,
      prepayCtaClicked
    );
  }

  continueWithRegistrationOnly(): void {
    posthog.capture(
      USPostHogTrackingEvents.ProtectionDetailsModalRegisterOnlyCtaClicked
    );

    this.modalReference.close(
      ProtectionDetailsUpsellModalResult.RegistrationOnly
    );
  }
}
